<script setup>
import { computed, ref } from 'vue';
import { format, fromUnixTime } from 'date-fns';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  slaEvents: {
    type: Array,
    default: () => [],
  },
});

const { t } = useI18n();

const isExpanded = ref(false);

const missesCount = computed(() => props.slaEvents.length);

const EVENT_CONFIG = {
  frt: {
    label: () => t('SLA_REPORTS.MISS_TYPES.FRT'),
    bgClass: 'bg-red-500/10',
    textClass: 'text-red-500',
  },
  nrt: {
    label: () => t('SLA_REPORTS.MISS_TYPES.NRT'),
    bgClass: 'bg-amber-400/10',
    textClass: 'text-amber-400',
  },
  rt: {
    label: () => t('SLA_REPORTS.MISS_TYPES.RT'),
    bgClass: 'bg-orange-500/10',
    textClass: 'text-orange-500',
  },
};

const formatEventDate = timestamp =>
  format(fromUnixTime(timestamp), 'MMM d, yyyy, hh:mm a');

const getEventConfig = type =>
  EVENT_CONFIG[type] ?? {
    label: () => type,
    bgClass: 'bg-n-slate-3',
    textClass: 'text-n-slate-11',
  };
</script>

<template>
  <div class="w-full pb-3">
    <button
      type="button"
      class="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full transition-opacity hover:opacity-80 bg-red-100 text-red-700"
      @click="isExpanded = !isExpanded"
    >
      <span>{{ missesCount }} {{ $t('SLA_REPORTS.MISSES_COUNT') }}</span>
      <i
        class="i-lucide-chevron-down size-4 transition-transform"
        :class="{ 'rotate-180': isExpanded }"
      />
    </button>

    <transition
      enter-active-class="transition-all duration-200 ease-out"
      enter-from-class="opacity-0 max-h-0"
      enter-to-class="opacity-100 max-h-96"
      leave-active-class="transition-all duration-150 ease-in"
      leave-from-class="opacity-100 max-h-96"
      leave-to-class="opacity-0 max-h-0"
    >
      <div
        v-if="isExpanded"
        class="overflow-hidden mt-2 rounded-lg border border-n-weak bg-n-solid-2"
      >
        <div class="flex items-center px-4 py-2.5 border-b border-n-weak">
          <span class="text-sm font-medium text-n-slate-11">
            {{ $t('SLA_REPORTS.MISS_PANEL_TITLE') }}
          </span>
        </div>
        <ul class="divide-y divide-n-weak">
          <li
            v-for="event in slaEvents"
            :key="event.id"
            class="flex items-center justify-between gap-3 px-4 py-2.5"
          >
            <span
              class="inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full"
              :class="[
                getEventConfig(event.event_type).bgClass,
                getEventConfig(event.event_type).textClass,
              ]"
            >
              {{ getEventConfig(event.event_type).label() }}
            </span>
            <span class="text-sm tabular-nums text-n-slate-11">
              {{ formatEventDate(event.created_at) }}
            </span>
          </li>
        </ul>
      </div>
    </transition>
  </div>
</template>
